// src/services/authService.js
import supabase from '../supabaseClient';
import { getUserPlans } from './subscriptionService'; 

// A single redirect base used for all auth emails/flows
const REDIRECT_BASE = (
  process.env.REACT_APP_SITE_URL ||
  process.env.FRONTEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000')
).replace(/\/+$/, '');

export const AUTH_OUTCOMES = Object.freeze({
  CONFIRMATION_REQUIRED: 'confirmation_required',
  EXISTING_ACCOUNT: 'existing_account',
  SIGNED_IN: 'signed_in',
});

export function normalizeEmail(email = '') {
  return email.trim().toLowerCase();
}

function makeError(message, code = 'auth_error', originalError = null) {
  const e = new Error(message);
  e.code = code;
  e.originalError = originalError;
  return e;
}

function isAlreadyRegisteredError(err) {
  if (['email_exists', 'user_already_exists', 'identity_already_exists', 'already_registered'].includes(err?.code)) {
    return true;
  }
  const msg = (err?.message || '').toLowerCase();
  return (
    /already registered/.test(msg) ||
    /user already exists/.test(msg) ||
    /duplicate/i.test(msg)
  );
}

function getErrorCode(err) {
  if (err?.code) return err.code;

  const message = (err?.message || '').toLowerCase();
  if (/invalid login credentials/.test(message)) return 'invalid_credentials';
  if (/email not confirmed/.test(message)) return 'email_not_confirmed';
  if (/rate limit|too many requests/.test(message)) return 'over_request_rate_limit';
  if (/failed to fetch|network|load failed/.test(message)) return 'network_error';
  return 'auth_error';
}

export function toUserFacingAuthError(err, fallbackMessage = 'Die Anfrage konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.') {
  if (isAlreadyRegisteredError(err)) {
    return makeError('Diese E-Mail ist bereits registriert.', 'already_registered', err);
  }

  const code = getErrorCode(err);
  const messages = {
    invalid_credentials: 'E-Mail oder Passwort ist nicht korrekt.',
    email_not_confirmed: 'Ihre E-Mail-Adresse ist noch nicht bestätigt.',
    email_address_invalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    weak_password: 'Das Passwort erfüllt die Sicherheitsanforderungen noch nicht.',
    same_password: 'Das neue Passwort muss sich vom bisherigen Passwort unterscheiden.',
    signup_disabled: 'Neue Registrierungen sind derzeit deaktiviert.',
    email_provider_disabled: 'Die Anmeldung per E-Mail ist derzeit nicht verfügbar.',
    provider_disabled: 'Diese Anmeldemethode ist derzeit nicht verfügbar.',
    captcha_failed: 'Die Sicherheitsprüfung ist fehlgeschlagen. Bitte laden Sie die Seite neu.',
    over_email_send_rate_limit: 'Zu viele E-Mails wurden angefordert. Bitte warten Sie mindestens 60 Sekunden.',
    over_request_rate_limit: 'Zu viele Versuche. Bitte warten Sie kurz und versuchen Sie es erneut.',
    request_timeout: 'Die Anfrage hat zu lange gedauert. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    network_error: 'Supabase ist gerade nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung.',
    session_not_found: 'Dieser Link ist ungültig oder abgelaufen. Fordern Sie bitte einen neuen Link an.',
  };

  return makeError(messages[code] || fallbackMessage, code, err);
}

function isEmailVerified(user) {
  if (!user) return false;
  if (user.email_confirmed_at) return true;
  if (user.confirmed_at) return true;
  if (Array.isArray(user.identities)) {
    const anyVerified = user.identities.some(
      (i) =>
        i?.identity_data?.email_verified === true ||
        i?.identity_data?.email_confirmed === true
    );
    if (anyVerified) return true;
  }
  return false;
}

export async function registerUser(email, password, metadata = {}) {
  const normalizedEmail = normalizeEmail(email);
  const { data, error } = await supabase.auth.signUp({
    email: normalizedEmail,
    password,
    options: {
      data: metadata,
      emailRedirectTo: `${REDIRECT_BASE}/login`,
    },
  });

  // Supabase may deliberately return an obfuscated user instead of an error for
  // an existing confirmed account. An empty identities array is the documented
  // client-side signal that no new identity was created.
  if (isAlreadyRegisteredError(error)) {
    return {
      user: null,
      session: null,
      email: normalizedEmail,
      outcome: AUTH_OUTCOMES.EXISTING_ACCOUNT,
    };
  }

  if (error) {
    throw toUserFacingAuthError(error, 'Das Konto konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
  }

  const identities = data?.user?.identities;
  const isExistingAccount = Array.isArray(identities) && identities.length === 0;
  const outcome = isExistingAccount
    ? AUTH_OUTCOMES.EXISTING_ACCOUNT
    : data?.session
      ? AUTH_OUTCOMES.SIGNED_IN
      : AUTH_OUTCOMES.CONFIRMATION_REQUIRED;

  return { ...data, email: normalizedEmail, outcome };
}

export async function loginUser(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: normalizeEmail(email),
    password,
  });

  if (error) {
    throw toUserFacingAuthError(error, 'Die Anmeldung ist gerade nicht möglich. Bitte versuchen Sie es erneut.');
  }

  const user = data?.user;
  if (!user) {
    throw makeError('Die Anmeldung konnte nicht abgeschlossen werden.', 'missing_user');
  }

  if (!isEmailVerified(user)) {
    await supabase.auth.signOut();
    throw makeError('Ihre E-Mail-Adresse ist noch nicht bestätigt.', 'email_not_confirmed');
  }

  return data;
}

export async function logoutUser() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return true;
  } catch (err) {
    throw err;
  }
}

export async function getSession() {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  } catch (err) {
    throw err;
  }
}

export async function sendPasswordResetEmail(email) {
  const { data, error } = await supabase.auth.resetPasswordForEmail(normalizeEmail(email), {
    redirectTo: `${REDIRECT_BASE}/reset-password`,
  });
  if (error) {
    throw toUserFacingAuthError(error, 'Die Reset-E-Mail konnte gerade nicht angefordert werden.');
  }
  return data;
}

// Supabase's default email links point at the project hostname.  We use the
// TokenHash in a link on our own domain instead, so confirmation and recovery
// emails do not contain a cross-domain auth URL that mailbox providers may
// treat as suspicious.
export async function verifyEmailToken(tokenHash, type = 'email') {
  if (!tokenHash) {
    throw makeError('Dieser Link ist ungültig oder abgelaufen.', 'session_not_found');
  }

  const { data, error } = await supabase.auth.verifyOtp({
    token_hash: tokenHash,
    type,
  });

  if (error) {
    throw toUserFacingAuthError(error, 'Dieser Link ist ungültig oder abgelaufen.');
  }

  return data;
}

export async function resendVerificationEmail(email) {
  const { data, error } = await supabase.auth.resend({
    type: 'signup',
    email: normalizeEmail(email),
    options: {
      emailRedirectTo: `${REDIRECT_BASE}/login`,
    },
  });
  if (error) {
    throw toUserFacingAuthError(error, 'Die Bestätigungs-E-Mail konnte gerade nicht erneut gesendet werden.');
  }
  return data;
}

export async function updatePassword(password) {
  const { data, error } = await supabase.auth.updateUser({ password });
  if (error) {
    throw toUserFacingAuthError(error, 'Das Passwort konnte nicht geändert werden. Bitte fordern Sie einen neuen Link an.');
  }
  return data;
}

export async function signInWithGoogle() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: REDIRECT_BASE, 
      },
    });
    if (error) throw error;
    return data;
  } catch (err) {
    throw err;
  }
}

export async function signInWithApple() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: REDIRECT_BASE,
      },
    });
    if (error) throw error;
    return data;
  } catch (err) {
    throw err;
  }
}

export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
}

export async function getUserProfile(userId) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) throw error;
    return data;
  } catch (err) {
    throw err;
  }
}

export async function logUserEvent(userId, eventType, details = {}) {
  try {
    let effectiveUserId = userId;
    if (!effectiveUserId) {
      const { data } = await supabase.auth.getUser();
      effectiveUserId = data?.user?.id || null;
    }

    if (!effectiveUserId) return false;

    const { error } = await supabase.from('user_events').insert([
      {
        user_id: effectiveUserId,
        event_type: eventType,
        details,
      },
    ]);
    if (error) throw error;
    return true;
  } catch (err) {
    return false;
  }
}

// --- UPDATED: DELETE ACCOUNT (Fixes 403 Error) ---
export async function deleteUserAccount() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Kein Benutzer angemeldet");

    // 1. DELETE AVATAR (Using API instead of SQL to avoid 403 error)
    try {
        const { data: profile } = await supabase
            .from('profiles')
            .select('avatar_url')
            .eq('id', user.id)
            .single();
            
        if (profile?.avatar_url) {
            console.log("Lösche Avatar...", profile.avatar_url);
            await supabase.storage
                .from('avatars')
                .remove([profile.avatar_url]);
        }
    } catch (storageErr) {
        console.warn("Avatar konnte nicht gelöscht werden:", storageErr);
        // Continue anyway
    }

    // 2. CHECK SUBSCRIPTIONS (Optional check)
    try {
       await getUserPlans();
    } catch (billingError) {
      console.warn("Warnung: Abo-Status konnte nicht geprüft werden", billingError);
    }

    // 3. DELETE USER (SQL)
    // Make sure you updated the SQL function in Supabase to remove the storage deletion line!
    const { error } = await supabase.rpc('delete_user_account');
    
    if (error) throw error;

    // 4. CLEAR SESSION
    await supabase.auth.signOut();
    
    return true;
  } catch (err) {
    console.error("Delete Account Error:", err);
    throw new Error("Konto konnte nicht gelöscht werden: " + err.message);
  }
}
